import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Maximize2, Minimize2, Minus, Plus, Scan } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { trackEvent } from "@/shared/analytics";
import { cn } from "@/shared/lib";
import { Button } from "@/shared/ui/button";

import { useAssetLookup } from "../../api/useAssetLookup";
import { buildMapScene } from "../../map/buildMapScene";
import type { MapFrame } from "../../map/drawMatchMap";
import { isDeadAt } from "../../map/isDeadAt";
import { MAP_LAYERS, type MapLayer } from "../../map/MAP_LAYERS";
import { mapFacts } from "../../map/mapFacts";
import type { MapMode } from "../../map/MapMode";
import { pointAt } from "../../map/pointAt";
import type { TimelineEvent } from "../../map/TimelineEvent";
import { adjacentEvent, openingMoment, timelineEvents } from "../../map/timelineEvents";
import type { MapGeometry, MatchMap } from "../../model/Analysis";
import { MapCanvas, type MapCanvasHandle } from "./MapCanvas";
import { MapFacts } from "./MapFacts";
import { MapLayerToggles } from "./MapLayerToggles";
import { MapLegend } from "./MapLegend";
import { MapModeToggle } from "./MapModeToggle";
import { TimeBar } from "./TimeBar";
import { TimelineEventCard } from "./TimelineEventCard";
import { useHeat } from "./useHeat";
import { useMapColors } from "./useMapColors";
import { useMapImages } from "./useMapImages";
import { usePlayback } from "./usePlayback";
import { ZOOM_KEY } from "./zoomKey";

const TITLE = msg({ id: "analysis.map.title", message: "Where {hero} was" });
const HINT = msg({
  id: "analysis.map.zoom_hint",
  message: "Your half is always at the bottom · {key} + wheel or pinch to zoom",
});
const LEGEND = msg({ id: "analysis.map.legend", message: "Legend" });
const ZOOM_IN = msg({ id: "analysis.map.zoom_in", message: "Zoom in" });
const ZOOM_OUT = msg({ id: "analysis.map.zoom_out", message: "Zoom out" });
const FIT = msg({ id: "analysis.map.fit", message: "Whole map" });
const FULLSCREEN = msg({ id: "analysis.map.fullscreen", message: "Full screen" });
const EXIT_FULLSCREEN = msg({ id: "analysis.map.exit_fullscreen", message: "Exit full screen" });

// Шаг кнопок «+» и «−».
const ZOOM_STEP = 1.5;

// Во весь экран: `native` — Fullscreen API на блок, `overlay` — блок поверх страницы, где
// API нет (iPhone) или браузер отказал.
type Fullscreen = "off" | "native" | "overlay";

type MatchMapViewProps = {
  match: MatchMap;
  geometry: MapGeometry;
};

// Тепловая карта матча по макету «Карта матча: правки по ревью»: где был игрок (тепло и
// хвост), лагеря, здания, смерти, руны, мид-босс, остальные игроки — на шкале времени матча.
// Открывается на первой смерти игрока с её карточкой.
export const MatchMapView = ({ match, geometry }: MatchMapViewProps) => {
  const { _ } = useLingui();
  const scene = useMemo(() => buildMapScene(match, geometry), [match, geometry]);
  const facts = useMemo(() => mapFacts(match), [match]);
  const events = useMemo(() => timelineEvents(match), [match]);
  const opening = useMemo(() => openingMoment(events, scene.durationS), [events, scene]);
  const hero = useAssetLookup("heroes")(scene.me.heroId);
  const colors = useMapColors();
  const loaded = useMapImages(scene, colors);
  const playback = usePlayback(scene.durationS, opening.t);
  const { t } = playback;
  const [mode, setMode] = useState<MapMode>("all");
  const [layers, setLayers] = useState<Record<MapLayer, boolean>>({ ...MAP_LAYERS });
  const [legendOpen, setLegendOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState<Fullscreen>("off");
  const blockRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<MapCanvasHandle>(null);
  const heat = useHeat(scene, t, mode);
  // Карточка события: под курсором шкалы или закреплённая после перехода к событию.
  const [hovered, setHovered] = useState<TimelineEvent | null>(null);
  const [pinned, setPinned] = useState<TimelineEvent | null>(opening.event);
  const shown = hovered ?? pinned;

  useEffect(() => {
    const onChange = () => {
      setFullscreen((current) =>
        document.fullscreenElement === blockRef.current
          ? "native"
          : current === "native"
            ? "off"
            : current,
      );
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
    };
  }, []);

  // Поверх страницы блок выходит из потока и прокрутка страницы сбивается: после закрытия
  // возвращаем страницу к карте. Escape закрывает, как и настоящий полный экран.
  const closeOverlay = () => {
    setFullscreen("off");
    requestAnimationFrame(() => {
      blockRef.current?.scrollIntoView({ block: "nearest" });
    });
  };
  useEffect(() => {
    if (fullscreen !== "overlay") {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeOverlay();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [fullscreen]);

  // Время на карте двигает только игрок: шкала, переход к событию, воспроизведение.
  const seek = (value: number) => {
    trackEvent("map_seek");
    playback.seek(value);
    setPinned(null);
  };
  const jump = (event: TimelineEvent) => {
    trackEvent("map_seek");
    playback.seek(event.at);
    setPinned(event);
  };
  const neighbour = (direction: 1 | -1) => {
    const event = adjacentEvent(events, pinned, t, direction);
    return event === null
      ? null
      : () => {
          jump(event);
        };
  };
  const enterFullscreen = () => {
    const block = blockRef.current;
    if (block === null) {
      return;
    }
    trackEvent("map_fullscreen");
    if (!document.fullscreenEnabled) {
      setFullscreen("overlay");
      return;
    }
    block.requestFullscreen().catch(() => {
      setFullscreen("overlay");
    });
  };
  const exitFullscreen = () => {
    if (fullscreen === "native") {
      void document.exitFullscreen();
    } else {
      closeOverlay();
    }
  };

  const frame: MapFrame = useMemo(
    () => ({
      t,
      mode,
      layers,
      highlight: shown === null ? null : { x: shown.x, y: shown.y },
    }),
    [t, mode, layers, shown],
  );
  const images = useMemo(() => ({ ...loaded, heat }), [loaded, heat]);
  const dead = isDeadAt(scene.me.deaths, t);
  const hp = dead ? null : (pointAt(scene.me.track, scene.stepS, t)?.[2] ?? null);
  const expanded = fullscreen !== "off";

  return (
    <section
      ref={blockRef}
      aria-labelledby="analysis-map-title"
      data-analysis-map
      data-fullscreen={fullscreen}
      className={cn(
        "@container flex flex-col gap-3 rounded-xl border border-border bg-surface p-3 @lg:p-4 @lg:px-5",
        fullscreen === "native" && "h-dvh rounded-none",
        fullscreen === "overlay" &&
          "fixed inset-0 z-50 h-dvh overflow-y-auto overscroll-contain rounded-none border-0",
      )}
    >
      <div className="flex flex-col gap-2.5">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <h2 id="analysis-map-title" className="font-display text-2xl leading-tight font-bold">
            {_({ ...TITLE, values: { hero: hero.name } })}
          </h2>
          <p className="text-xs text-subtle @max-lg:hidden">
            {_({ ...HINT, values: { key: ZOOM_KEY } })}
          </p>
        </div>
        <MapFacts facts={facts} />
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2.5">
          <div className="max-w-full min-w-0">
            <MapLayerToggles layers={layers} team={scene.team} onChange={setLayers} />
          </div>
          <MapModeToggle mode={mode} onChange={setMode} />
        </div>
      </div>
      <div
        className={cn(
          "relative overflow-hidden rounded-[10px] bg-map-backdrop",
          expanded ? "min-h-0 flex-1" : "aspect-square @lg:aspect-auto @lg:h-[560px]",
        )}
      >
        <MapCanvas
          ref={canvasRef}
          scene={scene}
          frame={frame}
          images={images}
          colors={colors}
          fullscreen={expanded}
          // Открытая легенда закрывает карту — карточку под ней не показываем.
          overlay={
            shown === null || legendOpen ? null : hovered !== null ? (
              <TimelineEventCard event={hovered} />
            ) : (
              <TimelineEventCard event={shown} onPrevious={neighbour(-1)} onNext={neighbour(1)} />
            )
          }
        />
        <div className="pointer-events-none absolute inset-2 flex flex-col items-end gap-2 @lg:inset-3">
          <div className="pointer-events-auto flex gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-expanded={legendOpen}
              onClick={() => {
                setLegendOpen(!legendOpen);
              }}
            >
              {_(LEGEND)}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={_(ZOOM_IN)}
              title={_(ZOOM_IN)}
              className="@max-lg:hidden"
              onClick={() => {
                canvasRef.current?.zoomBy(ZOOM_STEP);
              }}
            >
              <Plus aria-hidden="true" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={_(ZOOM_OUT)}
              title={_(ZOOM_OUT)}
              className="@max-lg:hidden"
              onClick={() => {
                canvasRef.current?.zoomBy(1 / ZOOM_STEP);
              }}
            >
              <Minus aria-hidden="true" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={_(FIT)}
              title={_(FIT)}
              onClick={() => {
                canvasRef.current?.fit();
              }}
            >
              <Scan aria-hidden="true" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={_(expanded ? EXIT_FULLSCREEN : FULLSCREEN)}
              title={_(expanded ? EXIT_FULLSCREEN : FULLSCREEN)}
              onClick={expanded ? exitFullscreen : enterFullscreen}
            >
              {expanded ? <Minimize2 aria-hidden="true" /> : <Maximize2 aria-hidden="true" />}
            </Button>
          </div>
          {legendOpen && (
            <MapLegend
              scene={scene}
              images={loaded}
              colors={colors}
              className="pointer-events-auto min-h-0 w-[min(32rem,100%)] overflow-y-auto"
            />
          )}
        </div>
      </div>
      <TimeBar
        t={t}
        durationS={scene.durationS}
        playing={playback.playing}
        speed={playback.speed}
        hp={hp}
        dead={dead}
        team={scene.team}
        events={events}
        shown={shown}
        onToggle={() => {
          trackEvent("map_seek");
          playback.toggle();
          setPinned(null);
        }}
        onSpeed={playback.nextSpeed}
        onSeek={seek}
        onJump={jump}
        onHover={setHovered}
      />
    </section>
  );
};
